import { type ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "./i18n";
import LanguageSwitcher from "./LanguageSwitcher";
import type { LocalAccount } from "./lib/localStore/accountScope";
import { LocalStoreClient } from "./lib/localStore/client";

export default function LocalAccountGate({
  account,
  canClaim,
  onSwitchAccount,
  children,
}: {
  account: LocalAccount;
  canClaim: boolean;
  onSwitchAccount: () => Promise<void>;
  children: ReactNode;
}) {
  const { t } = useTranslation("auth");
  const client = useRef<LocalStoreClient | null>(null);
  const [status, setStatus] = useState<"checking" | "pending" | "ready">("checking");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const connection = new LocalStoreClient(account);
    client.current = connection;
    let active = true;
    void connection
      .accountStatus()
      .then((result) => {
        if (active) setStatus(result === "pending" ? "pending" : "ready");
      })
      .catch((caught: unknown) => {
        if (active) setError(caught instanceof Error ? caught.message : t("localIsolationFailed"));
      });
    return () => {
      active = false;
      connection.close();
      client.current = null;
    };
  }, [account.workspaceId]);

  async function finish(claim: boolean) {
    if (busy || !client.current) return;
    setBusy(true);
    setError("");
    try {
      if (claim) {
        if (!canClaim) throw new Error(t("localIsolationVerify"));
        await client.current.claimLegacyWorkspace(email);
        // Old drafts are only made available to the explicitly confirmed owner.
        const oldWorkspace = JSON.stringify("local-workspace-v1");
        for (const key of Object.keys(localStorage)) {
          const fixed = [
            "classroom-local-selected-class",
            "classroom-free-beta-tour-v1",
            "classroom-trial-roster-tour-v1",
          ];
          const next = fixed.includes(key)
            ? `${key}:${account.workspaceId}`
            : key.startsWith("classroom-class-setup-v1:") && key.includes(oldWorkspace)
              ? key.replace(oldWorkspace, JSON.stringify(account.workspaceId))
              : null;
          if (!next) continue;
          const value = localStorage.getItem(key);
          if (value !== null && localStorage.getItem(next) === null) localStorage.setItem(next, value);
        }
      } else await client.current.deferLegacyWorkspace();
      setStatus("ready");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t("localIsolationFailed"));
    } finally {
      setBusy(false);
    }
  }

  if (status === "ready") return children;
  return (
    <main className="local-account-gate" id="main-content">
      <div className="console-language-bar">
        <LanguageSwitcher />
      </div>
      <section className="paper-card" aria-labelledby="local-account-title">
        <h1 id="local-account-title">{t(status === "pending" ? "localIsolationTitle" : "localIsolationChecking")}</h1>
        {status === "pending" && (
          <>
            <p>{t("localIsolationExplanation")}</p>
            <p>
              {t("localIsolationCurrent")} <strong>{account.email}</strong>
            </p>
            <p>{t("localIsolationOwnerNotice")}</p>
            <label>
              {t("localIsolationEmail")}
              <input
                type="email"
                autoComplete="off"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={busy}
              />
            </label>
            <div className="local-account-gate-actions">
              <button
                type="button"
                disabled={busy || !canClaim || email.trim().toLowerCase() !== account.email.toLowerCase()}
                onClick={() => void finish(true)}
              >
                {t("localIsolationClaim")}
              </button>
              <button type="button" disabled={busy} onClick={() => void finish(false)}>
                {t("localIsolationNew")}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  setBusy(true);
                  void onSwitchAccount().catch((caught: unknown) => {
                    setError(caught instanceof Error ? caught.message : t("localIsolationFailed"));
                    setBusy(false);
                  });
                }}
              >
                {t("localIsolationSwitch")}
              </button>
            </div>
            {!canClaim && <p role="status">{t("localIsolationVerify")}</p>}
          </>
        )}
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        {error && status === "checking" && (
          <button type="button" onClick={() => location.reload()}>
            {t("localIsolationRetry")}
          </button>
        )}
      </section>
    </main>
  );
}
